import { COL_INVENTORY_MOVEMENTS } from '../config/collections'
import { pb } from '../pocketbase'
import type { InventoryMovementRecord, ScheduleSlotRecord, ScheduleSlotStatus } from '../types/records'
import { bomQuantityForOutput, filterBomForDate } from './materialRequirements'
import { loadProductBomRows } from './productionRequestBom'

const REF_TYPE = 'schedule_slot'

async function existingReserves(slotId: string): Promise<InventoryMovementRecord[]> {
  return pb.collection(COL_INVENTORY_MOVEMENTS).getFullList<InventoryMovementRecord>({
    filter: `referenceType="${REF_TYPE}" && referenceId="${slotId}" && type="reserve"`,
  })
}

export async function reserveMaterialsForSlot(
  slot: Pick<ScheduleSlotRecord, 'id' | 'plannedStart' | 'plannedQuantity'>,
  productId: string,
  createdBy?: string,
): Promise<void> {
  const bom = filterBomForDate(await loadProductBomRows(productId), slot.plannedStart)
  const needs = bomQuantityForOutput(bom, slot.plannedQuantity)

  for (const [consumableId, { qty }] of needs) {
    if (qty <= 0) continue
    await pb.collection(COL_INVENTORY_MOVEMENTS).create({
      consumable: consumableId,
      type: 'reserve',
      quantity: qty,
      referenceType: REF_TYPE,
      referenceId: slot.id,
      createdBy: createdBy || undefined,
    })
  }
}

export async function unreserveMaterialsForSlot(
  slotId: string,
  createdBy?: string,
): Promise<void> {
  const reserves = await existingReserves(slotId)
  for (const m of reserves) {
    const qty = Math.abs(Number(m.quantity) || 0)
    if (qty <= 0) continue
    await pb.collection(COL_INVENTORY_MOVEMENTS).create({
      consumable: m.consumable,
      type: 'unreserve',
      quantity: qty,
      referenceType: REF_TYPE,
      referenceId: slotId,
      notes: 'Снятие резерва',
      createdBy: createdBy || undefined,
    })
  }
}

export async function issueMaterialsForSlot(
  slot: Pick<ScheduleSlotRecord, 'id' | 'plannedStart' | 'plannedQuantity' | 'actualQuantity'>,
  productId: string,
  createdBy?: string,
): Promise<void> {
  const outputQty =
    slot.actualQuantity != null ? Number(slot.actualQuantity) : Number(slot.plannedQuantity) || 0
  const bom = filterBomForDate(await loadProductBomRows(productId), slot.plannedStart)
  const needs = bomQuantityForOutput(bom, outputQty)

  for (const [consumableId, { qty }] of needs) {
    if (qty <= 0) continue
    await pb.collection(COL_INVENTORY_MOVEMENTS).create({
      consumable: consumableId,
      type: 'issue',
      quantity: qty,
      referenceType: REF_TYPE,
      referenceId: slot.id,
      createdBy: createdBy || undefined,
    })
  }

  await unreserveMaterialsForSlot(slot.id, createdBy)
}

export async function onSlotStatusChange(
  slot: ScheduleSlotRecord,
  productId: string,
  prevStatus: ScheduleSlotStatus,
  nextStatus: ScheduleSlotStatus,
  createdBy?: string,
): Promise<void> {
  if (prevStatus === nextStatus) return

  if (nextStatus === 'in_progress' && prevStatus !== 'in_progress') {
    await reserveMaterialsForSlot(slot, productId, createdBy)
    return
  }

  if (nextStatus === 'completed') {
    await issueMaterialsForSlot(slot, productId, createdBy)
    return
  }

  if (nextStatus === 'cancelled') {
    await unreserveMaterialsForSlot(slot.id, createdBy)
  }
}

export async function loadSlotInventoryMovements(slotId: string): Promise<InventoryMovementRecord[]> {
  if (!slotId) return []
  return pb.collection(COL_INVENTORY_MOVEMENTS).getFullList<InventoryMovementRecord>({
    filter: `referenceType="${REF_TYPE}" && referenceId="${slotId}"`,
    expand: 'consumable',
    sort: 'created',
  })
}
